/**
 * Standalone note form for a patient.
 * Renders a type selector, then delegates to the appropriate sub-form.
 * When `noteId` is provided the form operates in edit mode — submission
 * PATCHes the existing note instead of creating a new one.
 */

import { CheckCircle } from 'lucide-react'
import { useState } from 'react'
import { toast } from 'sonner'

import type { FileResponse, NoteContent, NoteWithStaff } from '@snootsvet/shared'
import { isPlainTextNoteType, LAB_TYPES, type LabType, type NoteType } from '@snootsvet/shared'

import { useAppForm } from '@/frontend/components/form'
import { FileAttachments } from '@/frontend/components/form/file-attachments'
import { FormLabel } from '@/frontend/components/form/primitives'
import { StaffSearch } from '@/frontend/components/staff-search'
import { Button } from '@/frontend/components/ui/button'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/frontend/components/ui/select'
import { useCreateNote } from '@/frontend/hooks/api/notes/use-create-note'
import { usePatchNote } from '@/frontend/hooks/api/notes/use-patch-note'
import { useGetAuthenticatedStaff } from '@/frontend/hooks/api/use-get-authenticated-staff'
import { useFileAttachments } from '@/frontend/hooks/use-file-attachments'

/** Note types available when creating a new standalone note. */
const CREATABLE_NOTE_TYPES: { value: NoteType; label: string; fileRequired: boolean }[] = [
  { value: 'general', label: 'General', fileRequired: false },
  { value: 'internal', label: 'Internal', fileRequired: false },
  { value: 'lab_results', label: 'Lab Results', fileRequired: false },
  { value: 'clinical', label: 'Clinical', fileRequired: false },
  { value: 'medical_history', label: 'Medical History', fileRequired: false },
  { value: 'external_prescription', label: 'External Prescription', fileRequired: true },
]

/**
 * All note types the form can handle (create + edit).
 * Types like soap, follow_up, and phone_call are only reachable via editing
 * an existing note — they aren't shown in the create dropdown.
 */
const ALL_NOTE_TYPES: { value: NoteType; label: string; fileRequired: boolean }[] = [
  ...CREATABLE_NOTE_TYPES,
  { value: 'soap', label: 'SOAP', fileRequired: false },
  { value: 'follow_up', label: 'Follow-up', fileRequired: false },
  { value: 'phone_call', label: 'Phone Call', fileRequired: false },
]

interface NoteFormProps {
  patientId: string
  /** When provided the form operates in edit mode. */
  noteId?: string
  /** Existing note data — required when `noteId` is set. */
  note?: NoteWithStaff
  onSuccess?: () => void
  onCancel?: () => void
}

export function NoteForm({ patientId, noteId, note, onSuccess, onCancel }: NoteFormProps) {
  const isEditing = noteId !== undefined
  const [type, setType] = useState<NoteType>(note?.type ?? 'general')
  const { data: me } = useGetAuthenticatedStaff()
  // Author credited on the note. Defaults to the existing author when editing,
  // otherwise to the current user once their staff record loads.
  const [writtenBy, setWrittenBy] = useState<string | null>(note?.writtenBy ?? null)
  const effectiveWrittenBy = writtenBy ?? me?.id ?? null
  const selectedOption = ALL_NOTE_TYPES.find((o) => o.value === type)!
  // Only a standalone plain-text note can switch, and only to another plain-text type.
  const canChangeType = !note || (!note.consultationId && isPlainTextNoteType(note.type))
  const dropdownOptions = !isEditing
    ? CREATABLE_NOTE_TYPES
    : canChangeType
      ? ALL_NOTE_TYPES.filter((o) => isPlainTextNoteType(o.value))
      : ALL_NOTE_TYPES

  const subFormProps: SubFormProps = {
    patientId,
    noteId,
    existingFiles: note?.files,
    type,
    writtenBy: effectiveWrittenBy,
    fileRequired: selectedOption.fileRequired,
    onSuccess: onSuccess ?? (() => {}),
    onCancel,
  }

  return (
    <div className="flex min-w-0 flex-col gap-4 py-2">
      {/* Type selector — lives outside the sub-forms. Switching to a different form shape
          (SOAP / Lab Results) remounts the sub-form, but switching between text-based types
          keeps the same GenericNoteForm mounted so drafted text is preserved. */}
      <div className="flex flex-col gap-1.5">
        <FormLabel required>Type</FormLabel>
        <Select
          value={type}
          onValueChange={(value) => setType(value ?? 'general')}
          disabled={!canChangeType}
          items={dropdownOptions.map((o) => ({ label: o.label, value: o.value }))}
        >
          <SelectTrigger className="w-full">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {dropdownOptions.map((opt) => (
              <SelectItem key={opt.value} value={opt.value}>
                {opt.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        {type === 'internal' && (
          <p className="text-sm text-muted-foreground">
            This note will not be visible to the patient.
          </p>
        )}
        {type === 'medical_history' && (
          <p className="text-sm text-muted-foreground">
            Attach records from a previous practice as PDFs or images to get an AI summary.
          </p>
        )}
      </div>

      {/* Author selector — who the note is credited to. Defaults to the current
          user on new notes but can be reassigned to any staff member. */}
      <div className="flex flex-col gap-1.5">
        <FormLabel required>Author</FormLabel>
        <StaffSearch
          value={effectiveWrittenBy}
          onChange={(staff) => setWrittenBy(staff?.id ?? null)}
          placeholder="Select author"
        />
      </div>

      {type === 'soap' ? (
        <SoapNoteForm
          key={noteId ?? 'soap'}
          {...subFormProps}
          defaultValues={note?.type === 'soap' ? getSoapDefaults(note) : undefined}
        />
      ) : type === 'lab_results' ? (
        <LabResultsNoteForm
          key={noteId ?? 'lab_results'}
          {...subFormProps}
          defaultValues={note?.type === 'lab_results' ? getLabResultsDefaults(note) : undefined}
        />
      ) : (
        <GenericNoteForm
          // Stable key across all text-based types so switching between them (e.g. General →
          // Internal) keeps the form mounted and preserves the drafted text instead of clearing it.
          key={noteId ?? 'generic'}
          {...subFormProps}
          originalContent={note?.content}
          defaultValues={
            note && note.type !== 'soap' && note.type !== 'lab_results'
              ? getGenericDefaults(note)
              : undefined
          }
        />
      )}
    </div>
  )
}

// ---------------------------------------------------------------------------
// Shared subform types + logic
// ---------------------------------------------------------------------------

/** Shared props passed down to each sub-form. */
interface SubFormProps {
  patientId: string
  noteId?: string
  existingFiles?: FileResponse[]
  type: NoteType
  /** Staff id credited as the note's author. */
  writtenBy: string | null
  fileRequired: boolean
  onSuccess: () => void
  onCancel?: () => void
}

interface UseSubmitNoteOptions {
  patientId: string
  noteId?: string
  type: NoteType
  writtenBy: string | null
  existingFiles?: FileResponse[]
  onSuccess: () => void
}

/**
 * Encapsulates shared note submission logic (mutations, file handling, toasts)
 * so that each sub-form only needs to provide its content-specific fields.
 */
function useSubmitNote({
  patientId,
  noteId,
  type,
  writtenBy,
  existingFiles,
  onSuccess,
}: UseSubmitNoteOptions) {
  const isEditing = noteId !== undefined
  const { mutateAsync: createNote } = useCreateNote()
  const { mutateAsync: patchNote } = usePatchNote()
  const {
    uploading,
    uploadPending,
    removeFileIds,
    reset: resetFiles,
    fileCountError,
    attachmentProps,
  } = useFileAttachments({
    existingFiles: existingFiles?.map((f) => ({ id: f.id, fileName: f.fileName })),
  })

  const hasFiles =
    attachmentProps.uploadedKeys.length > 0 ||
    attachmentProps.pendingFiles.length > 0 ||
    (existingFiles?.length ?? 0) - removeFileIds.length > 0

  const submit = async (
    content: NoteContent,
    options?: { onSuccess?: () => void }
  ): Promise<string | undefined> => {
    try {
      if (isEditing) {
        const addFileKeys = await uploadPending()
        await patchNote({
          id: noteId,
          body: {
            type,
            content,
            ...(writtenBy ? { writtenBy } : {}),
            ...(addFileKeys.length > 0 ? { addFileKeys } : {}),
            ...(removeFileIds.length > 0 ? { removeFileIds } : {}),
          },
        })
        toast('Note updated', { icon: <CheckCircle className="size-4 text-success-fg" /> })
      } else {
        const fileKeys = await uploadPending()
        await createNote({
          patientId,
          type,
          content,
          ...(writtenBy ? { writtenBy } : {}),
          ...(fileKeys.length > 0 ? { fileKeys } : {}),
        })
        options?.onSuccess?.()
        resetFiles()
        toast('Note added', { icon: <CheckCircle className="size-4 text-success-fg" /> })
      }
      onSuccess()
      return undefined
    } catch (err) {
      return err instanceof Error
        ? err.message
        : `Failed to ${isEditing ? 'update' : 'create'} note`
    }
  }

  return { isEditing, uploading, hasFiles, fileCountError, attachmentProps, submit }
}

// ---------------------------------------------------------------------------
// Generic sub-form — used for all text-based note types
// ---------------------------------------------------------------------------

interface GenericDefaults {
  text: string
}

function getGenericDefaults(
  note: NoteWithStaff & { type: Exclude<NoteType, 'soap' | 'lab_results'> }
): GenericDefaults {
  return { text: note.content.text }
}

function GenericNoteForm({
  patientId,
  noteId,
  existingFiles,
  type,
  writtenBy,
  fileRequired,
  originalContent,
  defaultValues,
  onSuccess,
  onCancel,
}: SubFormProps & { defaultValues?: GenericDefaults; originalContent?: NoteContent }) {
  const { isEditing, uploading, hasFiles, fileCountError, attachmentProps, submit } = useSubmitNote(
    {
      patientId,
      noteId,
      type,
      writtenBy,
      existingFiles,
      onSuccess,
    }
  )

  const form = useAppForm({
    defaultValues: defaultValues ?? { text: '' },
    onSubmit: async ({ value, formApi }) => {
      if (fileRequired && !hasFiles) {
        formApi.setErrorMap({
          onSubmit: { form: 'A file attachment is required for this note type', fields: {} },
        })
        return
      }
      if (fileCountError) {
        formApi.setErrorMap({ onSubmit: { form: fileCountError, fields: {} } })
        return
      }
      // Merge with original content to preserve fields not in the form (e.g. follow_up taskId)
      const content = { ...originalContent, text: value.text }
      const error = await submit(content, { onSuccess: () => formApi.reset() })
      if (error) {
        formApi.setErrorMap({ onSubmit: { form: error, fields: {} } })
      }
    },
  })

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault()
        form.handleSubmit()
      }}
    >
      <form.AppForm>
        <div className="flex min-w-0 flex-col gap-4 py-2">
          <form.GlobalError />
          <form.AppField name="text">
            {(field) => (
              <field.RichTextField label="Note" placeholder="Write your note here…" required />
            )}
          </form.AppField>
          <FileAttachments {...attachmentProps} required={fileRequired} />
        </div>
        <div className="flex items-center justify-end gap-2 pt-3">
          {onCancel && (
            <Button variant="outline" size="lg" type="button" onClick={onCancel}>
              Cancel
            </Button>
          )}
          <form.SubmitButton disabled={uploading || !!fileCountError}>
            {isEditing ? 'Save Changes' : 'Add Note'}
          </form.SubmitButton>
        </div>
      </form.AppForm>
    </form>
  )
}

// ---------------------------------------------------------------------------
// SOAP sub-form
// ---------------------------------------------------------------------------

interface SoapDefaults {
  subjective: string
  objective: string
  assessment: string
  plan: string
}

function getSoapDefaults(note: NoteWithStaff & { type: 'soap' }): SoapDefaults {
  return {
    subjective: note.content.subjective ?? '',
    objective: note.content.objective ?? '',
    assessment: note.content.assessment ?? '',
    plan: note.content.plan ?? '',
  }
}

function SoapNoteForm({
  patientId,
  noteId,
  existingFiles,
  type,
  writtenBy,
  defaultValues,
  onSuccess,
  onCancel,
}: SubFormProps & { defaultValues?: SoapDefaults }) {
  const { isEditing, uploading, fileCountError, attachmentProps, submit } = useSubmitNote({
    patientId,
    noteId,
    type,
    writtenBy,
    existingFiles,
    onSuccess,
  })

  const form = useAppForm({
    defaultValues: defaultValues ?? { subjective: '', objective: '', assessment: '', plan: '' },
    onSubmit: async ({ value, formApi }) => {
      if (fileCountError) {
        formApi.setErrorMap({ onSubmit: { form: fileCountError, fields: {} } })
        return
      }
      const error = await submit(value, { onSuccess: () => formApi.reset() })
      if (error) {
        formApi.setErrorMap({ onSubmit: { form: error, fields: {} } })
      }
    },
  })

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault()
        form.handleSubmit()
      }}
    >
      <form.AppForm>
        <div className="flex min-w-0 flex-col gap-4 py-2">
          <form.GlobalError />
          <form.AppField name="subjective">
            {(field) => (
              <field.RichTextField
                label="Subjective"
                placeholder="Patient history, owner concerns…"
              />
            )}
          </form.AppField>
          <form.AppField name="objective">
            {(field) => (
              <field.RichTextField label="Objective" placeholder="Examination findings, vitals…" />
            )}
          </form.AppField>
          <form.AppField name="assessment">
            {(field) => (
              <field.RichTextField label="Assessment" placeholder="Diagnosis, differentials…" />
            )}
          </form.AppField>
          <form.AppField name="plan">
            {(field) => <field.RichTextField label="Plan" placeholder="Treatment, follow-up…" />}
          </form.AppField>
          <FileAttachments {...attachmentProps} />
        </div>
        <div className="flex items-center justify-end gap-2 pt-3">
          {onCancel && (
            <Button variant="outline" size="lg" type="button" onClick={onCancel}>
              Cancel
            </Button>
          )}
          <form.SubmitButton disabled={uploading || !!fileCountError}>
            {isEditing ? 'Save Changes' : 'Add Note'}
          </form.SubmitButton>
        </div>
      </form.AppForm>
    </form>
  )
}

// ---------------------------------------------------------------------------
// Lab results sub-form
// ---------------------------------------------------------------------------

interface LabResultsDefaults {
  labType: LabType
  clinicalNotes: string
  clientCommunication: string
}

function getLabResultsDefaults(note: NoteWithStaff & { type: 'lab_results' }): LabResultsDefaults {
  return {
    labType: note.content.labType,
    clinicalNotes: note.content.clinicalNotes ?? '',
    clientCommunication: note.content.clientCommunication ?? '',
  }
}

function LabResultsNoteForm({
  patientId,
  noteId,
  existingFiles,
  type,
  writtenBy,
  defaultValues,
  onSuccess,
  onCancel,
}: SubFormProps & { defaultValues?: LabResultsDefaults }) {
  const { isEditing, uploading, fileCountError, attachmentProps, submit } = useSubmitNote({
    patientId,
    noteId,
    type,
    writtenBy,
    existingFiles,
    onSuccess,
  })

  const form = useAppForm({
    defaultValues: defaultValues ?? {
      labType: 'bloods' as const,
      clinicalNotes: '',
      clientCommunication: '',
    },
    onSubmit: async ({ value, formApi }) => {
      if (fileCountError) {
        formApi.setErrorMap({ onSubmit: { form: fileCountError, fields: {} } })
        return
      }
      const error = await submit(value, { onSuccess: () => formApi.reset() })
      if (error) {
        formApi.setErrorMap({ onSubmit: { form: error, fields: {} } })
      }
    },
  })

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault()
        form.handleSubmit()
      }}
    >
      <form.AppForm>
        <div className="flex min-w-0 flex-col gap-4 py-2">
          <form.GlobalError />
          <form.AppField name="labType">
            {(field) => (
              <field.SelectField
                required
                label="Lab Type"
                placeholder="Select a type"
                items={LAB_TYPES}
              />
            )}
          </form.AppField>
          <form.AppField name="clinicalNotes">
            {(field) => (
              <field.RichTextField
                label="Clinical Notes"
                placeholder="Findings, interpretation, follow-up recommendations…"
              />
            )}
          </form.AppField>
          <form.AppField name="clientCommunication">
            {(field) => (
              <field.RichTextField
                label="Client Communication"
                placeholder="Message to the owner…"
              />
            )}
          </form.AppField>
          <FileAttachments {...attachmentProps} />
        </div>
        <div className="flex items-center justify-end gap-2 pt-3">
          {onCancel && (
            <Button variant="outline" size="lg" type="button" onClick={onCancel}>
              Cancel
            </Button>
          )}
          <form.SubmitButton disabled={uploading || !!fileCountError}>
            {isEditing ? 'Save Changes' : 'Add Note'}
          </form.SubmitButton>
        </div>
      </form.AppForm>
    </form>
  )
}
